import { supabase } from './supabase'
import { Todo, Settings } from '../types'

export const todoService = {
  // Get todos for current week
  async getCurrentWeekTodos(weekStartDate: Date): Promise<Todo[]> {
    const { data, error } = await supabase
      .from('todos')
      .select('*')
      .eq('week_start_date', weekStartDate.toISOString().split('T')[0])
      .order('day_of_week', { ascending: true })
      .order('start_time', { ascending: true })

    if (error) throw error
    return data || []
  },

  // Create new todo
  async createTodo(todo: Omit<Todo, 'id' | 'created_at' | 'updated_at'>): Promise<Todo> {
    const { data, error } = await supabase
      .from('todos')
      .insert(todo)
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Update todo
  async updateTodo(id: string, updates: Partial<Todo>): Promise<Todo> {
    const { data, error } = await supabase
      .from('todos')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Delete todo
  async deleteTodo(id: string): Promise<void> {
    const { error } = await supabase
      .from('todos')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // 添加：一次性删除所有 weekly_repeat 数据
  async deleteAllWeeklyRepeats(): Promise<number> {
    const { data, error } = await supabase
      .from('todos')
      .delete()
      .eq('weekly_repeat', true)
      .select('id')

    if (error) throw error
    return data ? data.length : 0
  },

  // Get weekly repeating todos to create for current week
  async getRepeatingTodos(): Promise<Todo[]> {
    const { data, error } = await supabase
      .from('todos')
      .select('*')
      .eq('weekly_repeat', true)

    if (error) throw error
    return data || []
  },

  // Get daily repeating todos (for cross-week auto creation)
  async getDailyRepeatingTodos(): Promise<Todo[]> {
    const { data, error } = await supabase
      .from('todos')
      .select('*')
      .eq('daily_repeat', true)

    if (error) throw error
    return data || []
  },

  // Query todos by series key (title, start_time, end_time, color)
  async getTodosBySeriesKey(params: { title: string; start_time: string; end_time: string; color: string }): Promise<Todo[]> {
    const { data, error } = await supabase
      .from('todos')
      .select('*')
      .eq('title', params.title)
      .eq('start_time', params.start_time)
      .eq('end_time', params.end_time)
      .eq('color', params.color)

    if (error) throw error
    return data || []
  },

  // Create weekly repeating todos for new week
  async createWeeklyRepeatingTodos(todo: Omit<Todo, 'id' | 'created_at' | 'updated_at'>): Promise<Todo> {
    // 双保险：清洗可能误传入的主键与时间戳
    const { id: _oldId, created_at: _oldCreatedAt, updated_at: _oldUpdatedAt, ...clean } = todo as any
    // 新周初始化必须为未完成
    const payload = { ...clean, completed: false }
    return this.createTodo(payload as Omit<Todo, 'id' | 'created_at' | 'updated_at'>)
  }
}

export const settingsService = {
  // Get user settings
  async getSettings(): Promise<Settings | null> {
    const { data, error } = await supabase
      .from('settings')
      .select('*')
      .limit(1)
      .maybeSingle() // Use maybeSingle() instead of single() to handle no results gracefully

    if (error) throw error
    return data
  },

  // Update settings
  async updateSettings(settings: Partial<Settings>): Promise<Settings> {
    // First, get existing settings or create new one
    const existing = await this.getSettings()

    if (existing) {
      const { data, error } = await supabase
        .from('settings')
        .update(settings)
        .eq('id', existing.id)
        .select()
        .single()

      if (error) throw error
      return data
    } else {
      const { data, error } = await supabase
        .from('settings')
        .insert(settings)
        .select()
        .single()

      if (error) throw error
      return data
    }
  }
}